// Parent Service - API calls for the parent panel (children + per-child planning)
import { useAuthStore } from '@/features/auth/stores/authStore';
import {
  StudentExamScheduleResponse,
  StudentRattrapageScheduleResponse,
  StudentRattrapageHistoryResponse,
} from '@/types/student.types';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`;

const getHeaders = () => {
  const tokens = useAuthStore.getState().tokens;
  const accessToken = tokens?.accessToken;
  return {
    'Content-Type': 'application/json',
    Authorization: accessToken ? `Bearer ${accessToken}` : '',
  };
};

export interface ParentChild {
  studentId: string;
  firstName: string;
  lastName: string;
  studentCode: string;
  relationship: string;
  isPrimary: boolean;
}

const parentGet = async <T>(path: string, params?: URLSearchParams): Promise<T> => {
  const query = params && params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE}${path}${query}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Erreur lors de la récupération des données');
  }
  return response.json();
};

export const fetchMyChildren = async (): Promise<ParentChild[]> =>
  parentGet<ParentChild[]>('/parent/children');

export const fetchChildFinancialAccounts = async (
  studentId: string
): Promise<import('@/services/financialService').StudentFinancialAccount[]> =>
  parentGet(`/parent/children/${studentId}/financial-accounts`);

export const fetchChildBankTransfers = async (
  studentId: string
): Promise<import('@/services/financialService').PendingBankTransfer[]> =>
  parentGet(`/parent/children/${studentId}/bank-transfers`);

export const fetchChildSchedule = async (
  studentId: string,
  startDate?: string,
  endDate?: string
): Promise<unknown[]> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  return parentGet(`/parent/children/${studentId}/schedule`, params);
};

export const fetchChildClasses = async (
  studentId: string,
  academicYearId?: string,
  semester?: number
): Promise<unknown[]> => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);
  if (semester !== undefined) params.append('semester', String(semester));
  return parentGet(`/parent/children/${studentId}/classes`, params);
};

export const fetchChildModuleGrades = async (
  studentId: string,
  moduleElementId: string
): Promise<unknown> =>
  parentGet(`/parent/children/${studentId}/module/${moduleElementId}/grades`);

export const fetchChildModuleAttendance = async (
  studentId: string,
  moduleElementId: string
): Promise<unknown> =>
  parentGet(`/parent/children/${studentId}/module/${moduleElementId}/attendance`);

export interface ChildAbsence {
  id: string;
  studentId: string;
  studentName: string;
  courseName: string;
  sessionDate: string;
  status: 'ABSENT_UNEXCUSED' | 'ABSENT_EXCUSED';
  justification: string | null;
}

export const fetchChildrenAbsences = async (limit?: number): Promise<ChildAbsence[]> => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', String(limit));
  return parentGet<ChildAbsence[]>('/parent/absences', params);
};

export const fetchChildExamSchedule = async (
  studentId: string,
  academicYearId?: string,
  semester?: number
): Promise<StudentExamScheduleResponse> => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);
  if (semester !== undefined) params.append('semester', String(semester));
  return parentGet(`/parent/children/${studentId}/exam-schedule`, params);
};

export const fetchChildRattrapageSchedule = async (
  studentId: string,
  academicYearId?: string,
  semester?: number
): Promise<StudentRattrapageScheduleResponse> => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);
  if (semester !== undefined) params.append('semester', String(semester));
  return parentGet(`/parent/children/${studentId}/rattrapage-schedule`, params);
};

export const fetchChildRattrapageHistory = async (
  studentId: string,
  academicYearId?: string
): Promise<StudentRattrapageHistoryResponse> => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);
  return parentGet(`/parent/children/${studentId}/rattrapage-history`, params);
};
